import {
  MarketMoverEmailNotifications,
  UserPreferences,
  UserState,
  InAppNotificationPreferences,
} from '@/stores/User/state'

export type UserGetters = {
  [K in keyof UserState]: (state: UserState) => UserState[K]
} & {
  client: (state: UserState) => UserState['user']['client']
  zoomLevel: (state: UserState) => number
  messagePreferences: (state: UserState) => UserPreferences['messagePreferences'] | undefined
  selectedUniverseIds: (state: UserState) => string[]
  userGroups: (state: UserState, getters: UserGetters) => string[]
  isAdmin: (state: UserState, getters: UserGetters) => boolean
  isDataAnalyst: (state: UserState, getters: UserGetters) => boolean
  isRacFlagEditor: (state: UserState, getters: UserGetters) => boolean
  isExperienceAccess: (state: UserState, getters: UserGetters) => boolean
  isTrial: (state: UserState, getters: UserGetters) => boolean
  attributes: (state: UserState) => UserState['user']['attributes']
  showMarketData: (state: UserState, getters: UserGetterReturnTypes) => boolean
  editingNotifications: (state: UserState) => UserPreferences['notifications']
  editingTimezone: (state: UserState) => string | undefined
  editingIssueType: (state: UserState) => string | undefined
  editingSettingsChanged: (state: UserState, getters: UserGetterReturnTypes) => boolean
  editingMarketMoverEmailNotifications: (state: UserState) => MarketMoverEmailNotifications | null
  clientId: (state: UserState) => string | undefined
  userId: (state: UserState) => string | undefined
  inAppNotificationPreferences: (state: UserState) => InAppNotificationPreferences | null
}

// Type that maps UserGetters function keys to their return types
export type UserGetterReturnTypes = {
  [K in keyof UserGetters]: UserGetters[K] extends (...args: any[]) => infer R ? R : never
}

export default {
  user: (state: UserState) => state.user,
  client: (state: UserState) => state.user?.client,
  userId: (state: UserState) => state.user?.attributes['custom:userId'],
  clientId: (state: UserState) => state.user?.attributes['custom:clientId'],
  preferences: (state: UserState) => state.preferences,
  zoomLevel: (state: UserState) => state.preferences?.sheetZoom || 1,
  messagePreferences: (state: UserState) => state.preferences?.messagePreferences,
  selectedUniverses: (state: UserState) => state.selectedUniverses,
  selectedUniverseIds: (state: UserState) => state.selectedUniverses?.map((item) => item.id) || [],
  licenseList: (state: UserState) => state.licenseList,
  userGroups: (state: UserState) => state?.user?.signInUserSession?.accessToken?.payload?.['cognito:groups'],
  isAdmin: (_, getters) =>
    !!(getters.userGroups as unknown as ReturnType<UserGetters['userGroups']>)?.includes('admin'),
  isDataAnalyst: (_, getters) =>
    (getters.userGroups as unknown as ReturnType<UserGetters['userGroups']>)?.includes('dataAnalyst'),
  isRacFlagEditor: (_, getters) =>
    (getters.userGroups as unknown as ReturnType<UserGetters['userGroups']>)?.includes('racFlagEditor'),
  isExperienceAccess: (_, getters) =>
    (getters.client as unknown as ReturnType<UserGetters['client']>)?.status === 'experience',
  isTrial: (_, getters) => (getters.client as unknown as ReturnType<UserGetters['client']>)?.status === 'trial',
  attributes: (state: UserState) => state?.user?.attributes,
  showMarketData: (_, getters) => (getters.client as unknown as ReturnType<UserGetters['client']>).hasInstruments,
  survey: (state: UserState) => state.survey,
  marketMoverEmailNotifications: (state: UserState) => state.marketMoverEmailNotifications,
  marketMoverEmailNotificationsDirty: (state: UserState) => state.marketMoverEmailNotificationsDirty,
  provisionalPreferences: (state: UserState) => state.provisionalPreferences,
  editingNotifications: (state: UserState) =>
    ({
      ...state.preferences?.notifications,
      ...state.provisionalPreferences?.notifications,
    }) as UserPreferences['notifications'],
  editingTimezone: (state: UserState) =>
    state.provisionalPreferences.timezone ? state.provisionalPreferences.timezone : state.preferences?.timezone,
  editingIssueType: (state: UserState) =>
    state.provisionalPreferences.issueType ? state.provisionalPreferences.issueType : state.preferences?.issueType,
  editingSettingsChanged: (_: UserState, getters: UserGetterReturnTypes) => {
    const editingTimezoneDifferent = getters.editingTimezone !== getters.preferences?.timezone
    const editingIssueTypeDifferent = getters.editingIssueType !== getters.preferences?.issueType
    const editingNotificationsDifferent =
      JSON.stringify(getters.editingNotifications) !== JSON.stringify(getters.preferences?.notifications)
    const editingMarketMoverDifferent = getters.marketMoverEmailNotificationsDirty
    const inAppNotificationPreferencesDifferent = getters.isInAppNotificationPreferencesDirty
    return (
      editingTimezoneDifferent ||
      editingIssueTypeDifferent ||
      editingNotificationsDifferent ||
      editingMarketMoverDifferent ||
      inAppNotificationPreferencesDifferent
    )
  },
  inAppNotificationPreferences: (state: UserState) => state.inAppNotificationPreferences,
  isInAppNotificationPreferencesDirty: (state: UserState) => state.isInAppNotificationPreferencesDirty,
  editingMarketMoverEmailNotifications: (state: UserState) => state.marketMoverEmailNotifications,
} satisfies UserGetters
